import React, { useCallback, useEffect, useState } from 'react';

import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import { type TableSortDirection } from '@amzn/meridian/table/table';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import { setSelectedFormTab } from 'src/store/adminConfigPageSlice/adminConfigPageSlice';
import { AdminConfigNavConsts } from 'src/store/adminConfigPageSlice/common/constants';
import {
  fetchRegions,
  setCurrentPage,
  setSearchField,
  setSelectedRegion,
  setTotalPages,
} from 'src/store/adminConfigPageSlice/regionTasksPageSlice/regionTasksPageSlice';
import { type FormDataRow } from 'src/store/adminConfigPageSlice/regionTasksPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';

interface OnSortProps {
  sortColumn: keyof FormDataRow;
  sortDirection: TableSortDirection;
}

/**
 * Default Component
 */
const RestrictionFormTable = () => {
  const dispatch = useAppDispatch();
  const [sortColumn, setSortColumn] = useState<keyof FormDataRow>('name');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const onSort = useCallback(({ sortColumn, sortDirection }: OnSortProps) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  }, []);

  const { filteredTableData, searchField, currentPage, perPage } = useAppSelector(
    (state) => state.regionTasksConfigPage,
  );

  const { userLoading } = useAppSelector((state) => state.user);
  const [sortedTableData, setSortedTableData] = useState(filteredTableData);

  useEffect(() => {
    const { paginatedData, totalPages } = filterSortAndPaginateTableData(
      filteredTableData,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      ['id'],
      perPage,
    );
    setSortedTableData(paginatedData as FormDataRow[]);
    dispatch(setTotalPages(totalPages));
  }, [sortColumn, sortDirection, filteredTableData, searchField, currentPage, perPage, dispatch]);

  // Go back to first page when filter/sort
  useEffect(() => {
    dispatch(setCurrentPage(1));
  }, [dispatch, searchField, sortColumn, sortDirection]);

  useEffect(() => {
    if (userLoading === 'fulfilled') {
      dispatch(fetchRegions());
    }
    return () => {
      dispatch(setSearchField(''));
    };
  }, [dispatch, userLoading]);

  return (
    <Box overflowY="scroll" overflowX="scroll" type="outline" className="view-and-edit-tasks__table">
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showDividers={true}
        fixHeaderRows={true}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        <TableRow>
          <TableCell sortColumn="name">State/Region/Province</TableCell>
          <TableCell sortColumn="country">Applicable Country</TableCell>
          <TableCell sortColumn="formName">Form Name</TableCell>
          <TableCell sortColumn="isActive">Is Active?</TableCell>
          <TableCell sortColumn="description">Description</TableCell>
          <TableCell>Edit</TableCell>
        </TableRow>
        {sortedTableData.map((row) => (
          <UncontrolledExpandableRow key={row.id} data-testid="restriction-form-table__row">
            <TableCell>{row.name}</TableCell>
            <TableCell>{row.country}</TableCell>
            <TableCell>{row.formName}</TableCell>
            <TableCell>{row.isActive ? 'Yes' : 'No'}</TableCell>
            <TableCell>{row.description}</TableCell>
            <TableCell>
              <Button
                type="icon"
                data-testid="view-and-edit-tasks__edit-icon"
                className="view-and-edit-tasks__edit-icon"
                onClick={() => {
                  dispatch(setSelectedRegion(row));
                  dispatch(setSelectedFormTab(AdminConfigNavConsts.Form.Edit.TAB));
                }}
              >
                <Icon tokens={editTokens}></Icon>
              </Button>
            </TableCell>
          </UncontrolledExpandableRow>
        ))}
      </Table>
    </Box>
  );
};

export default RestrictionFormTable;
